import { useState } from 'react';

import { PaymentStatusApplePay, CURRENCY_CODE, ApplePayButtonStatus } from 'constants/';
import { DeviceTypes } from 'constants/app';
import { useAmount, usePayment, useTranslate } from 'hooks';
import { useAppSelector } from 'store/hooks';
import { useValidateMerchantMutation } from 'store/payment/service';
import { detectDeviceType } from 'utils';

import { useApplePayAvailability } from './hooks';
import { StatusMessage, getButtonClasses } from './status-message';
import { getMerchantSession, performPayment } from './utils';

const CartStatus = {
  READY: 0,
  PAYMENT_IN_PROGRESS: 1,
  PAYMENT_SUCCESS: 2,
  PAYMENT_FAILURE: 3,
  PAYMENT_CANCEL: 4,
};

const ApplePay = ({ acquiringID }: { acquiringID: number }) => {
  const [cardStatus, setCardStatus] = useState(CartStatus.READY);
  const { formKey, paymentData } = useAppSelector((state) => state.payment);
  const { currentAmount, currentAmountForPayment } = useAmount();
  const { t } = useTranslate();

  const applePayButtonStatus = useApplePayAvailability();
  const [validateMerchant] = useValidateMerchantMutation();
  const { sendPayment, handlePaymentResult } = usePayment();

  const handleValidation = async (url: string) => {
    const validationResponse = await validateMerchant({ url }).unwrap();

    return getMerchantSession(validationResponse);
  };

  const onClick = async () => {
    setCardStatus(CartStatus.PAYMENT_IN_PROGRESS);

    try {
      const result = await performPayment({
        ID: paymentData?.ID || '',
        acquiringID: acquiringID.toString(),
        amount: currentAmount,
        fullAmount: currentAmountForPayment,
        currencyCode: CURRENCY_CODE,
        sendPayment,
        getMerchantValidation: handleValidation,
      });

      switch (result.status) {
        case PaymentStatusApplePay.SUCCESS:
          setCardStatus(CartStatus.PAYMENT_SUCCESS);
          break;
        case PaymentStatusApplePay.CANCEL:
          setCardStatus(CartStatus.PAYMENT_CANCEL);
          break;
        default:
          setCardStatus(CartStatus.PAYMENT_FAILURE);
      }

      // 3DS, редирект и перезагрузка данных — только после того, как шит закрыт
      if (result.response) {
        handlePaymentResult(result.response);
      }
    } catch (err) {
      // Сюда попадает только ошибка создания сессии, например вторая сессия поверх активной
      /* eslint-disable-next-line no-console */
      console.error('Apple Pay session error', err);
      setCardStatus(CartStatus.PAYMENT_FAILURE);
    }
  };

  if (!applePayButtonStatus || applePayButtonStatus === ApplePayButtonStatus.UNKNOWN) {
    return <StatusMessage formKey={formKey} message="Проверяем Apple Pay..." />;
  }

  return detectDeviceType() === DeviceTypes.IOS ? (
    <>
      <div className={getButtonClasses(formKey)}>
        <button
          type="button"
          className="apple-pay-button-with-text apple-pay-button-black-with-text"
          id="apple-pay"
          onClick={onClick}
        />
      </div>
      {cardStatus === CartStatus.PAYMENT_FAILURE && (
        <p className="mt-2 text-sm text-center text-base-red-100">{t('error')}</p>
      )}
    </>
  ) : null;
};

export { ApplePay };
